/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { useEffect } from "react";
import PropTypes from "prop-types";

// SEO configuration for different pages/sections
const SEO_CONFIG = {
  home: {
    title: "Sailing Ships NFT Collection - Premium Cosmic Digital Art Portfolio",
    description:
      "Discover the Sailing Ships NFT Collection - premium cosmic digital art featuring interactive 3D space adventures and unique sailing vessels from across the galaxy.",
    keywords:
      "NFT collection, sailing ships, cosmic art, digital collectibles, blockchain art, WebGL NFT, React portfolio, space-themed NFT, premium digital art, interactive NFT, cosmic sailing, XRPL NFT, 3D space game",
    image: "/blog/blog1.png",
    type: "website",
  },
  collection: {
    title: "NFT Collection - Cosmic Sailing Vessels | Sailing Ships NFT",
    description:
      "Explore our premium collection of cosmic sailing vessel NFTs. Each ship features unique traits, elemental affinities, and cosmic origins with stunning visual effects.",
    keywords:
      "NFT collection showcase, sailing ships gallery, cosmic vessels, digital art collection, NFT marketplace, blockchain collectibles, unique NFTs",
    image: "/images/realms/realms1.png",
    type: "website",
  },
  roadmap: {
    title: "Development Roadmap | Sailing Ships NFT Collection",
    description:
      "Follow our journey from genesis launch to animated NFTs and community platform development. See what's coming next for Sailing Ships NFT.",
    keywords:
      "NFT roadmap, project development, sailing ships future, community platform, animated NFTs, blockchain development",
    image: "/blog/blog2.png",
    type: "website",
  },
  about: {
    title: "About | Sailing Ships NFT - The Story Behind Cosmic Art",
    description:
      "Learn about the inspiration, technology, and vision behind Sailing Ships NFT Collection. Discover how we blend cosmic storytelling with cutting-edge tech.",
    keywords:
      "NFT creator story, sailing ships inspiration, digital art vision, blockchain art philosophy, cosmic storytelling",
    image: "/images/realms/realms2.png",
    type: "website",
  },
  blog: {
    title: "Blog | Sailing Ships NFT - Development Insights & Stories",
    description:
      "Read about NFT development, cosmic art creation, blockchain technology, and the journey of building the Sailing Ships collection.",
    keywords: "NFT blog, development insights, digital art creation, blockchain education, sailing ships stories",
    image: "/images/og-blog.jpg",
    type: "blog",
  },
  contact: {
    title: "Contact | Get in Touch with Sailing Ships NFT",
    description:
      "Connect with the Sailing Ships NFT team. Questions about the collection, collaborations, or technical support? We're here to help.",
    keywords: "NFT contact, sailing ships support, collaboration inquiries, technical help, community contact",
    image: "/images/og-contact.jpg",
    type: "website",
  },
};

/**
 * SEOHead Component - Dynamic SEO meta tag management
 * Updates document head with appropriate meta tags based on current page/section
 */
const SEOHead = ({
  page = "home",
  customTitle = null,
  customDescription = null,
  customImage = null,
  customKeywords = null,
}) => {
  useEffect(() => {
    const config = SEO_CONFIG[page] || SEO_CONFIG.home;
    const baseUrl = window.location.origin;

    // Use custom values if provided, otherwise use config
    const title = customTitle || config.title;
    const description = customDescription || config.description;
    const keywords = customKeywords || config.keywords;
    const image = customImage || config.image;
    const fullImageUrl = image.startsWith("http") ? image : `${baseUrl}${image}`;
    const currentUrl = window.location.href;

    // Update document title
    document.title = title;

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, content, attribute = "content") => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        if (selector.includes("property=")) {
          meta.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes("name=")) {
          meta.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute(attribute, content);
    };

    // Update basic meta tags
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', title);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="og:image"]', fullImageUrl);
    updateMetaTag('meta[property="og:url"]', currentUrl);
    updateMetaTag('meta[property="og:type"]', config.type);

    // Update Twitter Card tags
    updateMetaTag('meta[name="twitter:title"]', title);
    updateMetaTag('meta[name="twitter:description"]', description);
    updateMetaTag('meta[name="twitter:image"]', fullImageUrl);
    updateMetaTag('meta[name="twitter:url"]', currentUrl);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);

    // Add structured data for the current page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": config.type === "blog" ? "Blog" : "WebPage",
      name: title,
      description: description,
      url: currentUrl,
      image: fullImageUrl,
      author: {
        "@type": "Person",
        name: "jsaif",
        url: "https://github.com/jsaif",
      },
      publisher: {
        "@type": "Organization",
        name: "Sailing Ships NFT",
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/favicon.png`,
        },
      },
      mainEntity: {
        "@type": "CreativeWork",
        name: "Sailing Ships NFT Collection",
        description: "Premium cosmic digital art NFT collection",
        creator: {
          "@type": "Person",
          name: "jsaif",
        },
      },
    };

    // Update or create structured data script
    let structuredScript = document.querySelector('script[type="application/ld+json"][data-page="' + page + '"]');
    if (!structuredScript) {
      structuredScript = document.createElement("script");
      structuredScript.setAttribute("type", "application/ld+json");
      structuredScript.setAttribute("data-page", page);
      document.head.appendChild(structuredScript);
    }
    structuredScript.textContent = JSON.stringify(structuredData, null, 2);

    console.log(`🔍 SEO: Updated meta tags for page: ${page}`);
  }, [page, customTitle, customDescription, customImage, customKeywords]);

  return null; // This component doesn't render anything visible
};

// Higher-order component for easy SEO integration
export const withSEO = (WrappedComponent, seoConfig = {}) => {
  return function SEOWrappedComponent(props) {
    return (
      <>
        <SEOHead {...seoConfig} />
        <WrappedComponent {...props} />
      </>
    );
  };
};

// Hook for dynamic SEO updates
export const useSEO = (seoConfig) => {
  useEffect(() => {
    const seoHead = document.querySelector("[data-seo-component]");
    if (seoHead) {
      // Update SEO if component is already mounted
      seoHead.setAttribute("data-config", JSON.stringify(seoConfig));
    }
  }, [seoConfig]);
};

// Export SEO configuration for use in other components
export { SEO_CONFIG };

// PropTypes validation
SEOHead.propTypes = {
  page: PropTypes.string,
  customTitle: PropTypes.string,
  customDescription: PropTypes.string,
  customImage: PropTypes.string,
  customKeywords: PropTypes.string,
};

export default SEOHead;
